<template>
  <div
    v-if="showVerificationModal"
    class="fixed inset-0 flex items-center justify-center bg-black/70 z-50 px-4"
  >
    <div
      class="relative w-full max-w-3xl bg-[#121212] border border-[#00F0FF]/20 rounded-[16px] shadow-[0_0_30px_#00F0FF15] text-white overflow-hidden"
    >
      <!-- Close Button -->
      <button
        @click="$emit('close')"
        class="absolute top-3 right-5 text-white text-3xl font-bold hover:text-black transition-colors z-10"
      >
        &times;
      </button>

      <!-- Modal Header -->
      <div
        class="bg-gradient-to-r from-[#00F0FF] to-[#1ED6FF] p-4 text-black text-start text-lg font-bold uppercase tracking-wider rounded-t-2xl"
      >
        {{ $t('Modal.RaffleVerification.RaffleProof') }}
      </div>

      <!-- Modal Content -->
      <div class="p-6 text-sm space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
        <div>
          <h2 class="font-bold text-lg text-[#00F0FF]">{{ $t('Modal.RaffleVerification.ResultVerificationPage') }}</h2>
          <a
            href="https://api.random.org/signatures/form"
            target="_blank"
            class="text-[#4fc3f7] underline"
          >
            {{ $t('Modal.RaffleVerification.RandomOrgVerification') }}
          </a>
        </div>

        <div>
          <h3 class="font-bold text-lg text-[#00F0FF]">{{ $t('Modal.RaffleVerification.RaffleWinners') }}</h3>
          <ul class="bg-[#1A1D24] border border-white/5 p-4 rounded-lg text-green-400 text-sm space-y-2">
            <li v-if="raffleWinners.length === 0" class="text-center text-white/60">
              {{ $t('Modal.RaffleVerification.NoWinnersYet') }}
            </li>
            <li
              v-for="(winner, index) in raffleWinners"
              :key="index"
              :class="{
                'bg-[#2A2E38]': selectedWinner && selectedWinner._id === winner._id,
                'hover:bg-[#2A2E38]':
                  !selectedWinner || selectedWinner._id !== winner._id,
              }"
              class="py-3 px-4 rounded-xl transition-all cursor-pointer"
              @click="selectWinner(winner)"
            >
              <div class="flex items-center gap-3">
                <span class="text-2xl text-yellow-400">🎉</span>
                <div class="flex flex-col">
                  <div class="font-semibold text-base text-white">
                    {{ winner.user.username || $t('Modal.RaffleVerification.Anonymous') }}
                  </div>
                  <div class="text-xs text-white/60">
                    {{ $t('Modal.RaffleVerification.TicketID') }} {{ winner.ticketId }}
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div>
          <div class="flex justify-between items-center">
            <h3 class="font-bold text-lg text-[#00F0FF]">{{ $t('Modal.RaffleVerification.CodeSnippet') }}</h3>
            <button
              v-if="selectedWinnerProof"
              @click="
                copyToClipboard(
                  JSON.stringify(JSON.parse(selectedWinnerProof.proofBody), null, 2)
                )
              "
              class="text-white hover:text-[#00F0FF] transition text-xl"
            >
              📋
            </button>
          </div>
          <pre
            v-if="selectedWinnerProof"
            class="bg-[#1A1D24] border border-white/5 p-4 rounded-lg text-green-400 mt-2 text-xs overflow-auto"
            >{{ JSON.stringify(JSON.parse(selectedWinnerProof.proofBody), null, 2) }}
          </pre>
        </div>

        <div>
          <div class="flex justify-between items-center">
            <h3 class="font-bold text-lg text-[#00F0FF]">{{ $t('Modal.RaffleVerification.Signature') }}</h3>
            <button
              v-if="selectedWinnerProof"
              @click="copyToClipboard(selectedWinnerProof.proofSignature)"
              class="text-white hover:text-[#00F0FF] transition text-xl"
            >
              📋
            </button>
          </div>
          <pre
            v-if="selectedWinnerProof"
            class="bg-[#1A1D24] border border-white/5 p-4 rounded-lg text-gray-400 mt-2 text-xs overflow-auto"
            >{{ selectedWinnerProof.proofSignature }}
          </pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    showVerificationModal: Boolean,
    raffleWinners: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedWinnerProof: null, 
      selectedWinner: null, 
    };
  },
  methods: {
    async selectWinner(winner) {
      const raffleId = this.$route.params.id; 
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/raffles/${raffleId}/proof?entryId=${winner._id}`,
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          this.selectedWinnerProof = response.data.proofs[0]; 
          this.selectedWinner = winner; 
        } else {
          alert("Failed to fetch winner proof.");
        }
      } catch (error) {
        console.error("Error fetching winner proof:", error);
        alert("Error fetching winner proof.");
      }
    },

    // Method to copy content to clipboard
    copyToClipboard(content) {
      const el = document.createElement("textarea");
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      // Optional: Show a toast or notification to indicate successful copy
      alert("Content copied to clipboard!");
    },
  },
  watch: {
    // Watch for changes in raffleWinners and log them
    raffleWinners(newWinners) {
      console.log("Raffle Winners:", newWinners);
    },
    // Watch for when the modal is shown and log the winners
    showVerificationModal(newVal) {
      if (newVal) {
        console.log("Verification Modal opened, raffle winners:", this.raffleWinners);
      }
    },
  },
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #1A1D24;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #00f0ff;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #1ed6ff;
}
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #00f0ff #1A1D24;
}
</style>
